import {
  SchemaDatapointConstraints,
  SchemaDatapointUiConfig,
  SchemaItem,
  SchemaSection,
} from '@rossum/api-client/schemas';

export type AutomationSchemaField = {
  label: string;
  /**
   * Lodash-compatible path to this node inside the nested schema content,
   * e.g. `['0', 'children', '2']`.
   */
  path: string[];
  constraints?: SchemaDatapointConstraints | null;
  uiConfiguration?: SchemaDatapointUiConfig | null;
};

export type AutomationSchemaMap = ReadonlyMap<string, AutomationSchemaField>;

const collect = (
  node: SchemaSection | SchemaItem,
  path: string[],
  acc: Map<string, AutomationSchemaField>
): void => {
  acc.set(node.id, {
    label: node.label,
    path,
    constraints: 'constraints' in node ? node.constraints : undefined,
    uiConfiguration:
      'uiConfiguration' in node ? node.uiConfiguration : undefined,
  });

  if (!('children' in node) || !node.children) {
    return;
  }

  // Sections and tuples hold an array of children, multivalues a single object.
  // The path only gains an index in the array case.
  if (Array.isArray(node.children)) {
    node.children.forEach((child, index) =>
      collect(child, [...path, 'children', `${index}`], acc)
    );
  } else {
    collect(node.children, [...path, 'children'], acc);
  }
};

/**
 * Flat, id-keyed view of the nested schema content, carrying the lodash path
 * back to each node so thresholds can be read and written in place.
 */
export const buildAutomationSchemaMap = (
  content: SchemaSection[]
): AutomationSchemaMap => {
  const acc = new Map<string, AutomationSchemaField>();
  content.forEach((section, index) => collect(section, [`${index}`], acc));
  return acc;
};
